import type { IconButtonProps } from './icon-button.types'
import { useClickAnimation } from '../hooks/use-click-animation'
import { Tooltip, TooltipTrigger, TooltipPopup } from './tooltip'
import { cn } from '../lib/utils'
import {
  getButtonIconSize,
  isIcon,
  isLabel,
  processChildrenTree,
  processIcon,
} from '../lib/children-utils'
import { LoadingContent } from '../lib/loading-content'
import { Button as BaseButton } from '@base-ui/react/button'
import { mergeProps } from '@base-ui/react/merge-props'
import { cva } from 'class-variance-authority'
import flattenChildren from 'react-keyed-flatten-children'
import { isValidElement } from 'react'
import type * as React from 'react'

// On a touch screen every icon button hits in at least 44×44px (WCAG 2.5.5 / Apple HIG) through a centred
// pseudo-element, whatever its visual size; the combobox and number-field triggers use the same pattern.
const iconButtonVariants = cva(
  'focus-visible:ring-primary/80 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-[background-color,color,border-color] duration-300 ease-out outline-none select-none focus-visible:ring-4 focus-visible:ring-offset-4 disabled:cursor-not-allowed [@media(hover:none)]:disabled:pointer-events-none pointer-coarse:after:absolute pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11',
  {
    variants: {
      semanticRole: {
        primary: '',
        secondary: '',
        success: '',
        error: '',
        warning: '',
        info: '',
      },
      variant: {
        text: '',
        filled: '',
        outlined: 'border-border bg-surface-2 border',
      },
      size: {
        default: 'size-10',
        sm: 'size-6',
        lg: 'size-14',
      },
    },
    compoundVariants: [
      { variant: 'text', semanticRole: 'primary', className: 'text-primary' },
      {
        variant: 'outlined',
        semanticRole: 'primary',
        className: 'text-primary',
      },
      {
        variant: 'filled',
        semanticRole: 'primary',
        className: 'bg-primary text-on-primary',
      },
      {
        variant: 'text',
        semanticRole: 'secondary',
        className: 'text-secondary',
      },
      {
        variant: 'outlined',
        semanticRole: 'secondary',
        className: 'text-secondary bg-transparent',
      },
      {
        variant: 'filled',
        semanticRole: 'secondary',
        className: 'bg-secondary text-on-secondary',
      },
      { variant: 'text', semanticRole: 'success', className: 'text-success' },
      {
        variant: 'outlined',
        semanticRole: 'success',
        className: 'border-success-border bg-surface-success text-success',
      },
      {
        variant: 'filled',
        semanticRole: 'success',
        className: 'bg-success text-on-success',
      },
      { variant: 'text', semanticRole: 'error', className: 'text-error' },
      {
        variant: 'outlined',
        semanticRole: 'error',
        className: 'border-error-border bg-surface-error text-error',
      },
      {
        variant: 'filled',
        semanticRole: 'error',
        className: 'bg-error text-on-error',
      },
      { variant: 'text', semanticRole: 'warning', className: 'text-warning' },
      {
        variant: 'outlined',
        semanticRole: 'warning',
        className: 'border-warning-border bg-surface-warning text-warning',
      },
      {
        variant: 'filled',
        semanticRole: 'warning',
        className: 'bg-warning text-on-warning',
      },
      { variant: 'text', semanticRole: 'info', className: 'text-info' },
      {
        variant: 'outlined',
        semanticRole: 'info',
        className: 'border-info-border bg-surface-info text-info',
      },
      {
        variant: 'filled',
        semanticRole: 'info',
        className: 'bg-info text-on-info',
      },
    ],
    defaultVariants: {
      semanticRole: 'primary',
      variant: 'text',
      size: 'default',
    },
  },
)



function IconButton({
  className,
  children,
  semanticRole,
  variant,
  size,
  loading,
  disabled,
  gradient,
  toggled,
  tooltipSide,
  tooltipSideOffset,
  tooltipClassName,
  render,
  nativeButton,
  ...props
}: IconButtonProps) {
  const clickAnimation = useClickAnimation()

  const [icon, label] = validateAndExtractChildren(children)

  let processedIcon: React.ReactNode = icon
  const iconSize = getButtonIconSize({
    size,
    variant,
    defaultContainedSize: 20,
  })
  if (iconSize !== undefined) processedIcon = processIcon(icon, iconSize)

  if (toggled !== undefined && isValidElement(processedIcon)) {
    processedIcon = processChildrenTree(processedIcon, {
      iconFill: toggled ? 'currentColor' : 'none',
    })
  }

  // oxlint-disable-next-line typescript/no-base-to-string -- label is ReactNode that may include strings; String() is intentional for aria-label
  const labelString = String(label)

  const buttonElement = (
    <BaseButton
      className={cn(
        iconButtonVariants({
          semanticRole,
          variant,
          size,
        }),
        loading ? '!cursor-wait' : 'disabled:opacity-60',
        gradient && 'bg-gradient text-light-utility',
        toggled !== undefined && [
          variant === 'filled' &&
            (toggled
              ? 'bg-primary text-on-primary'
              : 'bg-secondary text-on-secondary'),
          variant === 'outlined' &&
            (toggled ? 'text-primary' : 'text-secondary'),
          variant === 'text' && (toggled ? 'text-primary' : 'text-secondary'),
        ],
        clickAnimation.className,
        className,
      )}
      data-slot="icon-button"
      data-testid={labelString}
      aria-label={labelString}
      // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- intentional: boolean || boolean, disabled can be false
      disabled={disabled || loading}
      render={render}
      nativeButton={
        nativeButton ?? !(isValidElement(render) && render.type === 'a')
      }
      {...mergeProps(clickAnimation.handlers, props)}
    >
      <LoadingContent loading={loading}>{processedIcon}</LoadingContent>
    </BaseButton>
  )

  return (
    <Tooltip>
      <TooltipTrigger render={buttonElement} />
      <TooltipPopup className={tooltipClassName} side={tooltipSide} sideOffset={tooltipSideOffset}>
        {label}
      </TooltipPopup>
    </Tooltip>
  )
}

function validateAndExtractChildren(children: React.ReactNode) {
  const flattened = flattenChildren(children)

  if (flattened.length === 0)
    throw new Error('you forgot to pass children to IconButton.')

  if (flattened.length > 2) {
    throw new Error(
      `you passed too many children to IconButton - ${flattened.length}. Expected exactly 2: an icon and a label.`,
    )
  }

  if (flattened.length !== 2) {
    throw new Error(
      'IconButton expects exactly 2 children: an icon and a label.',
    )
  }

  let icon: React.ReactNode | null = null
  let label: React.ReactNode | null = null

  for (const child of flattened) {
    if (isIcon(child)) {
      if (icon !== null)
        throw new Error(
          'IconButton accepts only one icon - you passed multiple.',
        )
      icon = child
    } else if (isLabel(child)) {
      if (label !== null)
        throw new Error(
          'IconButton expects only one label - you passed multiple.',
        )
      label = child
    } else {
      const childType = isValidElement(child) ? 'React element' : typeof child
      throw new Error(
        `IconButton: invalid child type (${childType}). Children must be an icon (React component with displayName ending in "Icon") and a label (string or number).`,
      )
    }
  }

  if (icon === null) {
    throw new Error('IconButton must receive an icon.')
  }

  if (label === null) {
    throw new Error('IconButton must receive a label.')
  }

  return [icon, label]
}

IconButton.renderable = true

export { IconButton }
